<template>
  <li ref="root" class="shrink-0 grow" :class="[gridItemClass, sizeClass]">
    <RevButtonBase
      class="relative block h-full w-auto"
      :class="{
        'focus-visible-outline-inset-hi cursor-pointer': props.allowMediaViewer,
      }"
      @click="onClick"
      @focusin.passive="onEnter"
      @focusout.passive="onLeave"
      @mouseenter.passive="onEnter"
      @mouseleave.passive="onLeave"
    >
      <div v-if="image.src" class="relative h-full w-full">
        <RevIllustration
          v-if="image.src"
          :alt="image.alt"
          class="rounded-lg block aspect-square object-cover"
          :class="sizeClass"
          :decoding="
            index < AMOUNT_OF_IMAGES_THAT_SHOULD_LOAD_EAGERLY - 1
              ? 'sync'
              : 'async'
          "
          :height="328"
          :loading="
            index < AMOUNT_OF_IMAGES_THAT_SHOULD_LOAD_EAGERLY - 1
              ? 'eager'
              : 'lazy'
          "
          :sizes="sizesAttr"
          :src="image.src"
          :width="328"
        />
        <Sticker
          v-if="withSticker"
          class="absolute right-0 top-0"
          size="small"
        />
      </div>
      <RevSkeleton
        v-else
        class="rounded-lg block aspect-square"
        :class="sizeClass"
        shape="rectangle"
      />
      <slot />
    </RevButtonBase>
  </li>
</template>

<script setup lang="ts">
import { computed, onUnmounted, useTemplateRef } from 'vue'

import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import { tw } from '@ds/utils/tailwind'
import { useIntersectionObserver } from '@vueuse/core'

import { isBig } from '../../Carousel/utils/isBig'
import {
  ALT_IMG_SIZES_LARGE,
  ALT_IMG_SIZES_SMALL,
  BIG_ALT_ITEM_SIZE_CLASS,
  SMALL_ALT_ITEM_SIZE_CLASS,
} from '../GalleryNew.constants'
import type { GalleryImage } from '../GalleryNew.types'

import Sticker from './Sticker/Sticker.vue'

const props = defineProps<{
  image: GalleryImage
  index: number
  totalLength: number
  allowMediaViewer: boolean
  // Only used for tracking and for resetting the active index, not for layout
  isMobile: boolean
  // Used to take into account injected extra items in the
  // size calculation (isBig)
  extraItemsIndexes: number[]
  withSticker?: boolean
}>()

const emit = defineEmits<{
  (e: 'click', index: number): void
  (e: 'enter', index: number): void
  (e: 'leave', index: number): void
  (e: 'visible', index: number): void
}>()

/**
 * Images loaded in the first load in a desktop carousel. For mobile, it's more
 * than what we need. We might improve this in the future.
 */
const AMOUNT_OF_IMAGES_THAT_SHOULD_LOAD_EAGERLY = 4

const onClick = () => {
  emit('click', props.index)
}

const onEnter = () => {
  emit('enter', props.index)
}

const onLeave = () => {
  emit('leave', props.index)
}

const root = useTemplateRef('root')

const { stop } = useIntersectionObserver(
  root,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting && intersectionRatio > 0.7 && props.isMobile) {
      // stagger the visible event to avoid race conditions
      // on images appearing at the same time. We want
      // to ensure the events are emitted in the correct order.
      setTimeout(() => {
        emit('visible', props.index)
      }, props.index * 10)
      stop()
    }
  },
  {
    threshold: 0.7,
  },
)

onUnmounted(() => {
  stop()
})

// =============================================
// Items classes
// =============================================
const isLargeItem = computed(() => {
  // -1 because the first item is always big
  // and isBig starts counting at 0
  const isLarge = isBig(
    props.index - 1,
    props.totalLength,
    props.extraItemsIndexes,
  )

  return isLarge
})

const gridItemClass = computed(() => {
  if (isLargeItem.value) {
    return [
      tw`[grid-row:span_2] [grid-column:span_2]`,
      tw`md:[grid-row:initial] md:[grid-column:initial]`,
    ]
  }

  return [
    tw`[grid-row:span_1] [grid-column:span_1]`,
    tw`md:[grid-row:initial] md:[grid-column:initial]`,
  ]
})

const sizesAttr = computed(() => {
  if (isLargeItem.value) {
    return ALT_IMG_SIZES_LARGE
  }

  return ALT_IMG_SIZES_SMALL
})

const sizeClass = computed(() => {
  if (isLargeItem.value) {
    return BIG_ALT_ITEM_SIZE_CLASS
  }

  return SMALL_ALT_ITEM_SIZE_CLASS
})
</script>
